import { z } from 'zod';
import { PROTOCOL_VERSION, ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from '../constants.js';

/**
 * Protocole WebSocket client ↔ serveur (spec §5.1).
 *
 * C'est la frontière de confiance du réseau : **tout** message est validé par
 * ces schémas **des deux côtés** — le serveur ne fait jamais confiance au
 * client, et le client ne fait jamais confiance au serveur (un serveur d'une
 * autre version doit produire une erreur propre, pas un `undefined` silencieux).
 *
 * Le protocole est entièrement GÉNÉRIQUE : ni Polo, ni Mag, ni magnétisme. Les
 * spécificités du jeu voyagent dans `flags: Record<string, number>` (drapeaux
 * publiés par le GameModule) et dans l'identifiant de niveau.
 */

// ---------------------------------------------------------------------------
// Briques communes
// ---------------------------------------------------------------------------

const codeAlphabet = new Set(ROOM_CODE_ALPHABET.split(''));

/** Code de room : 5 lettres de l'alphabet sans ambiguïté (spec §5.2). */
export const RoomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(ROOM_CODE_LENGTH)
  .refine(
    (value) => [...value].every((char) => codeAlphabet.has(char)),
    `le code ne peut contenir que les lettres ${ROOM_CODE_ALPHABET}`,
  );

/** Index de personnage dans le duo : 0 = `P`, 1 = `M`. */
export const CharIndexSchema = z.union([z.literal(0), z.literal(1)]);

/** Drapeaux d'état sérialisables, publiés par le GameModule. */
export const FlagsSchema = z.record(z.string(), z.number());

export const PlayerInputsSchema = z.object({
  left: z.boolean(),
  right: z.boolean(),
  jump: z.boolean(),
  action: z.boolean(),
  moveX: z.number().min(-1).max(1).optional(),
});

/** Vue réseau d'un robot : le strict nécessaire pour rejouer et interpoler. */
export const NetPlayerSchema = z.object({
  x: z.number(),
  y: z.number(),
  vx: z.number(),
  vy: z.number(),
  onGround: z.boolean(),
  facing: z.union([z.literal(-1), z.literal(1)]),
  dead: z.boolean(),
  atExit: z.boolean(),
  /** Mémoire du front montant de saut — indispensable à la réconciliation. */
  jumpHeld: z.boolean(),
  energy: z.number(),
  fieldActive: z.boolean(),
  flags: FlagsSchema,
  /** Entrées appliquées par le serveur à ce tick (rejeu du partenaire). */
  inputs: PlayerInputsSchema,
});

/** Vue réseau d'une entité (bloc, porte, ascenseur, boulon…). */
export const NetEntitySchema = z.object({
  id: z.string().min(1),
  x: z.number(),
  y: z.number(),
  vx: z.number(),
  vy: z.number(),
  solid: z.boolean(),
  flags: FlagsSchema,
});

/** Événement ponctuel produit par la simulation autoritaire. */
export const NetEventSchema = z.object({
  type: z.enum(['death', 'respawn', 'won']),
  tick: z.number().int().nonnegative(),
});

export const BoltCountSchema = z.object({
  collected: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

/** Un membre de la room, tel que publié dans `lobbyState`. */
export const LobbyPlayerSchema = z.object({
  memberId: z.string().min(1),
  slot: z.union([z.literal(0), z.literal(1)]),
  charIndex: CharIndexSchema.nullable(),
  ready: z.boolean(),
  connected: z.boolean(),
  host: z.boolean(),
});

/** Phase d'une room (spec §5.2). */
export const RoomPhaseSchema = z.enum(['lobby', 'countdown', 'playing', 'ended']);

/** Motif d'une mise en pause. */
export const PauseReasonSchema = z.enum(['peer-disconnected', 'manual']);

/** Codes d'erreur applicatifs — stables, testables, jamais traduits côté serveur. */
export const NetErrorCodeSchema = z.enum([
  'BAD_MESSAGE',
  'PROTOCOL_VERSION',
  'UNKNOWN_GAME',
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'ROOM_CLOSED',
  'NOT_IN_ROOM',
  'NOT_HOST',
  'CHAR_TAKEN',
  'UNKNOWN_LEVEL',
  'BAD_STATE',
  'RATE_LIMIT',
]);

export type NetErrorCode = z.infer<typeof NetErrorCodeSchema>;

// ---------------------------------------------------------------------------
// Client → Serveur
// ---------------------------------------------------------------------------

export const HelloMessageSchema = z.object({
  type: z.literal('hello'),
  protocolVersion: z.number().int().positive(),
});

export const CreateRoomMessageSchema = z.object({
  type: z.literal('createRoom'),
  gameId: z.string().min(1),
});

export const JoinRoomMessageSchema = z.object({
  type: z.literal('joinRoom'),
  code: RoomCodeSchema,
});

/** Reprise d'une place laissée libre par une déconnexion (fenêtre de grâce). */
export const RejoinRoomMessageSchema = z.object({
  type: z.literal('rejoinRoom'),
  code: RoomCodeSchema,
  memberId: z.string().min(1),
  token: z.string().min(1),
});

export const PickCharMessageSchema = z.object({
  type: z.literal('pickChar'),
  charIndex: CharIndexSchema,
});

export const PickLevelMessageSchema = z.object({
  type: z.literal('pickLevel'),
  levelId: z.string().min(1),
});

export const ReadyMessageSchema = z.object({
  type: z.literal('ready'),
  ready: z.boolean().default(true),
});

/** Entrées d'un tick — flux non fiable : le dernier reçu gagne (spec §5.1). */
export const InputMessageSchema = z.object({
  type: z.literal('input'),
  seq: z.number().int().nonnegative(),
  tick: z.number().int().nonnegative(),
  left: z.boolean(),
  right: z.boolean(),
  jump: z.boolean(),
  action: z.boolean(),
  moveX: z.number().min(-1).max(1).optional(),
});

export const PauseRequestMessageSchema = z.object({
  type: z.literal('pauseRequest'),
  paused: z.boolean().default(true),
});

export const LeaveRoomMessageSchema = z.object({
  type: z.literal('leaveRoom'),
});

/** Recommencer le niveau en cours (partie bloquée) — pour les DEUX joueurs. */
export const RestartRequestMessageSchema = z.object({
  type: z.literal('restartRequest'),
});

export const ClientMessageSchema = z.discriminatedUnion('type', [
  HelloMessageSchema,
  CreateRoomMessageSchema,
  JoinRoomMessageSchema,
  RejoinRoomMessageSchema,
  PickCharMessageSchema,
  PickLevelMessageSchema,
  ReadyMessageSchema,
  InputMessageSchema,
  PauseRequestMessageSchema,
  LeaveRoomMessageSchema,
  RestartRequestMessageSchema,
]);

export type ClientMessage = z.infer<typeof ClientMessageSchema>;
export type InputMessage = z.infer<typeof InputMessageSchema>;

// ---------------------------------------------------------------------------
// Serveur → Clients
// ---------------------------------------------------------------------------

export const ConnectedMessageSchema = z.object({
  type: z.literal('connected'),
  protocolVersion: z.number().int().positive(),
  clientId: z.string().min(1),
});

const RoomIdentitySchema = z.object({
  roomId: z.string().min(1),
  code: RoomCodeSchema,
  memberId: z.string().min(1),
  /** Jeton de reprise de place, conservé côté client (localStorage). */
  token: z.string().min(1),
  slot: z.union([z.literal(0), z.literal(1)]),
  host: z.boolean(),
  gameId: z.string().min(1),
});

export const RoomCreatedMessageSchema = RoomIdentitySchema.extend({
  type: z.literal('roomCreated'),
});

export const RoomJoinedMessageSchema = RoomIdentitySchema.extend({
  type: z.literal('roomJoined'),
  /** `true` quand il s'agit d'une reprise de place après déconnexion. */
  resumed: z.boolean().default(false),
});

export const LobbyStateMessageSchema = z.object({
  type: z.literal('lobbyState'),
  roomId: z.string().min(1),
  code: RoomCodeSchema,
  phase: RoomPhaseSchema,
  gameId: z.string().min(1),
  players: z.array(LobbyPlayerSchema).max(2),
  levelId: z.string().nullable(),
  paused: z.boolean(),
});

export const CountdownMessageSchema = z.object({
  type: z.literal('countdown'),
  /** Secondes restantes ; 0 = départ imminent. */
  seconds: z.number().int().nonnegative(),
  levelId: z.string().min(1),
});

export const GameStartMessageSchema = z.object({
  type: z.literal('gameStart'),
  levelId: z.string().min(1),
  seed: z.number().int(),
  startTick: z.number().int().nonnegative(),
});

export const SnapshotMessageSchema = z.object({
  type: z.literal('snapshot'),
  tick: z.number().int().nonnegative(),
  /** Dernier `seq` d'entrée pris en compte pour chaque place. */
  lastSeq: z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
  players: z.tuple([NetPlayerSchema, NetPlayerSchema]),
  /**
   * Entités CHANGÉES depuis le snapshot précédent (delta-friendly, spec §5.2).
   * `full = true` quand la liste est exhaustive (départ, reprise, respawn).
   */
  entities: z.array(NetEntitySchema),
  full: z.boolean(),
  status: z.enum(['playing', 'won']),
  deaths: z.number().int().nonnegative(),
  respawnTicks: z.number().int().nonnegative(),
  stars: z.number().int().nonnegative(),
  events: z.array(NetEventSchema).default([]),
});

export const GameEndMessageSchema = z.object({
  type: z.literal('gameEnd'),
  result: z.enum(['won', 'aborted']),
  timeMs: z.number().nonnegative(),
  bolts: BoltCountSchema,
  stars: z.number().int().nonnegative(),
  deaths: z.number().int().nonnegative(),
  levelId: z.string().min(1),
});

export const PeerDisconnectedMessageSchema = z.object({
  type: z.literal('peerDisconnected'),
  /** Secondes de grâce accordées ; **0 = départ définitif** (`leaveRoom`). */
  graceSeconds: z.number().int().nonnegative(),
  memberId: z.string().min(1),
});

export const PeerReconnectedMessageSchema = z.object({
  type: z.literal('peerReconnected'),
  memberId: z.string().min(1),
});

export const PausedMessageSchema = z.object({
  type: z.literal('paused'),
  reason: PauseReasonSchema,
  graceSeconds: z.number().int().nonnegative(),
});

export const ResumedMessageSchema = z.object({
  type: z.literal('resumed'),
  tick: z.number().int().nonnegative(),
});

export const RoomClosedMessageSchema = z.object({
  type: z.literal('roomClosed'),
  reason: z.enum(['ttl', 'grace-expired', 'host-left', 'empty', 'peer-left']),
});

export const ErrorMessageSchema = z.object({
  type: z.literal('error'),
  code: NetErrorCodeSchema,
  message: z.string(),
});

export const ServerMessageSchema = z.discriminatedUnion('type', [
  ConnectedMessageSchema,
  RoomCreatedMessageSchema,
  RoomJoinedMessageSchema,
  LobbyStateMessageSchema,
  CountdownMessageSchema,
  GameStartMessageSchema,
  SnapshotMessageSchema,
  GameEndMessageSchema,
  PeerDisconnectedMessageSchema,
  PeerReconnectedMessageSchema,
  PausedMessageSchema,
  ResumedMessageSchema,
  RoomClosedMessageSchema,
  ErrorMessageSchema,
]);

export type ServerMessage = z.infer<typeof ServerMessageSchema>;
export type SnapshotMessage = z.infer<typeof SnapshotMessageSchema>;
export type LobbyStateMessage = z.infer<typeof LobbyStateMessageSchema>;
export type GameEndMessage = z.infer<typeof GameEndMessageSchema>;
export type LobbyPlayer = z.infer<typeof LobbyPlayerSchema>;
export type RoomPhase = z.infer<typeof RoomPhaseSchema>;
export type PauseReason = z.infer<typeof PauseReasonSchema>;
export type NetPlayer = z.infer<typeof NetPlayerSchema>;
export type NetEntity = z.infer<typeof NetEntitySchema>;
export type NetEvent = z.infer<typeof NetEventSchema>;

// ---------------------------------------------------------------------------
// Sérialisation
// ---------------------------------------------------------------------------

/** Résultat d'un décodage : jamais d'exception, toujours un verdict. */
export type DecodeResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly error: string;
    };

function decode<T>(schema: z.ZodType<T>, raw: unknown): DecodeResult<T> {
  let json: unknown = raw;
  if (typeof raw === 'string') {
    try {
      json = JSON.parse(raw);
    } catch {
      return { ok: false, error: 'JSON invalide' };
    }
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    const detail = parsed.error.issues
      .map((issue) => `${issue.path.join('.') || '(racine)'} : ${issue.message}`)
      .join(' ; ');
    return { ok: false, error: detail };
  }
  return { ok: true, value: parsed.data };
}

/** Décode et valide un message CLIENT → SERVEUR (appelé par le serveur). */
export function decodeClientMessage(raw: unknown): DecodeResult<ClientMessage> {
  return decode(ClientMessageSchema, raw);
}

/** Décode et valide un message SERVEUR → CLIENT (appelé par le client). */
export function decodeServerMessage(raw: unknown): DecodeResult<ServerMessage> {
  return decode(ServerMessageSchema, raw);
}

/** Encode un message client, après validation (aucun message non conforme n'est émis). */
export function encodeClientMessage(message: ClientMessage): string {
  return JSON.stringify(ClientMessageSchema.parse(message));
}

/** Encode un message serveur, après validation. */
export function encodeServerMessage(message: ServerMessage): string {
  return JSON.stringify(ServerMessageSchema.parse(message));
}

/** Le client parle-t-il la même version du protocole que nous ? */
export function isSupportedProtocol(version: number): boolean {
  return version === PROTOCOL_VERSION;
}
